import { AppShell } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import Header from '../../molecules/Header.tsx';
import CookieConsentBanner from '../CookieConsentBanner/CookieConsentBanner.tsx';
import { ITabsProps, Navbar } from '../SideBar/SideBar.tsx';

import { useMemo } from 'react';
import { useNavProvider } from '../../../provider/navigationContext.tsx';

export interface IShellProps {
    children: React.ReactNode;
    tabs?: ITabsProps[] | null;
    route?: string;
    disabledTabs?: boolean;
}

const Shell: React.FC<IShellProps> = observer(props => {
    const { children } = props;
    const { opened } = useNavProvider();
    const location = useLocation();

    // Detect mobile devices
    const isSmallScreen = useMediaQuery('(max-width: 767px)');
    const isMediumScreen = useMediaQuery('(max-width: 1024px)');
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const isLandscape = useMediaQuery('(orientation: landscape)');
    const isTouchDevice = useMediaQuery('(pointer: coarse)');
    const isMobile = isSmallScreen && isPortrait && isTouchDevice;
    const isMobileLandscape = isSmallScreen && isLandscape && isTouchDevice;

    const mainStyles = useMemo(() => {
        let marginLeft = 0;

        if (props.route) {
            if (isMobile)
                marginLeft = 0;
             else if (isMobileLandscape) 
                marginLeft = opened ? 300 : 72;
             else if (isMediumScreen) {
                // For medium screens (tablets, smaller laptops)
                marginLeft = opened ? 250 : 72;
            } else {
                // For larger screens
                marginLeft = opened && location.pathname.includes('hcpPatientDashboard') ? 300 : opened ? 290 : 72;
            }
        }

        document.documentElement.style.setProperty('--navbar-width', `${marginLeft}px`);

        return {
            marginLeft,
            marginRight: 0,
            paddingInlineEnd: 0,
            paddingInlineStart: 0,
            paddingTop: 'calc(60px + var(--safe-area-inset-top, 0px))',
            paddingBottom: 'var(--safe-area-inset-bottom, 0px)',
            paddingLeft: 'var(--safe-area-inset-left, 0px)',
            paddingRight: 'var(--safe-area-inset-right, 0px)',
            backgroundColor: 'var(--mantine-color-gray-1)',
            overflow: 'visible',
            width: '100%',
            maxWidth: '100%',
        };
    }, [props.route, opened, isMobile, isMobileLandscape, isMediumScreen, location.pathname]);

    const appShellStyles = useMemo(
        () => ({
            root: {
                display: 'flex' as const,
                flexDirection: 'row' as const,
            },
            navbar: {
                position: 'fixed' as const,
                left: 0,
                right: 'auto',
                top: 'calc(60px + var(--safe-area-inset-top, 0px))',
                bottom: 'var(--audit-panel-height, 0px)',
                height: 'calc(100dvh - 60px - var(--safe-area-inset-top, 0px) - var(--audit-panel-height, 0px))',
                maxHeight: 'calc(100dvh - 60px - var(--safe-area-inset-top, 0px) - var(--audit-panel-height, 0px))',
            },
        }),
        []
    );

    return (
        <AppShell
            header={{ height: 'calc(60px + var(--safe-area-inset-top, 0px))' }}
            styles={{
                main: mainStyles,
                root: appShellStyles.root,
                navbar: appShellStyles.navbar,
            }}
            offsetScrollbars
            navbar={isMobile ? undefined : {
                width: 300,
                breakpoint: 'xs',
            }}
        >
            <Header />
            {props.tabs && <Navbar tabs={props.tabs} route={props.route} disabled={props.disabledTabs} />}
            <AppShell.Main>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100vh - 60px - var(--safe-area-inset-top, 0px) - var(--safe-area-inset-bottom, 0px) - var(--audit-panel-height, 0px))',
                        maxHeight: 'calc(100vh - 60px - var(--safe-area-inset-top, 0px) - var(--safe-area-inset-bottom, 0px) - var(--audit-panel-height, 0px))',
                        overflow: 'hidden',
                    }}
                >
                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</div>
                </div>
            </AppShell.Main>
            <CookieConsentBanner />
        </AppShell>
    );
});

export default Shell;
